/**
 * Lead Finder page (premium). Bundled into app.bundle.js via import from app.js.
 * Props: api (API.json), toast (showToast), onOpenLeads (navigate to Leads).
 */
const { useState, useEffect, useRef } = React;

const EXAMPLES = [
  'Find 20 plumbers in San Diego, CA',
  'HVAC companies in Escondido and San Marcos',
  'Family-owned restaurants in North Park, San Diego',
  'Auto repair shops in Austin, TX without a website'
];

const POLL_MS = 2000;
const LINE_TYPE_POLL_MS = 4000;
const LINE_TYPE_POLL_BUDGET_MS = 40000;

function defaultSelected(lead) {
  return !lead.existing_lead_id && !lead.dnc && !lead.toll_free && lead.source_verified;
}

function leadStatus(lead) {
  if (lead.existing_lead_id) return { label: 'Already a lead', cls: 'dnc' };
  if (lead.dnc) return { label: 'On DNC list', cls: 'not-interested' };
  if (lead.toll_free) return { label: 'Toll-free', cls: 'dnc' };
  if (!lead.source_verified) return { label: 'Unverified source', cls: 'not-interested' };
  return { label: lead.confidence === 'high' ? 'Verified' : 'Found', cls: lead.confidence === 'high' ? 'interested' : 'new' };
}

function formatPhone(e164) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 || '');
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

function hostLabel(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_) { return url; }
}

function errorMessage(err, fallback) {
  const d = err && err.data;
  return (d && (d.message || d.detail)) || (err && err.message) || fallback;
}

function leadsAddedText(count) {
  const n = Number(count) || 0;
  return n === 1 ? '1 lead added to Leads' : n + ' leads added to Leads';
}

function lineTypeCopy(phone, lineTypes) {
  const row = lineTypes && lineTypes[phone];
  const type = String((row && row.line_type) || '').trim().toLowerCase();
  if (!type || type === 'unknown') {
    return {
      label: 'Not checked yet',
      tone: 'pending',
      why: 'Phone type is checked after you import. Until then, the AI will not call this number. You can still call by hand.'
    };
  }
  if (type === 'landline') {
    return {
      label: 'Landline – AI can call',
      tone: 'ok',
      why: 'Consent rules allow the AI to call a verified business landline when the company name is on the lead.'
    };
  }
  if (type === 'mobile') {
    return {
      label: 'Mobile – needs consent for AI; you can call by hand',
      tone: 'warn',
      why: 'Consent rules require the person\'s consent on file before the AI calls a cell phone. You or your team can still call by hand.'
    };
  }
  if (type === 'fixedvoip' || type === 'nonfixedvoip' || type === 'voip') {
    return {
      label: 'Internet phone – needs consent for AI',
      tone: 'warn',
      why: 'Consent rules require consent on file before the AI calls an internet phone. You can still call by hand.'
    };
  }
  return {
    label: 'Needs consent for AI',
    tone: 'warn',
    why: 'This number is not a verified business landline, so the AI needs the person\'s consent on file. You can still call by hand.'
  };
}

function LineTypeBadge({ phone, lineTypes }) {
  const info = lineTypeCopy(phone, lineTypes);
  return (
    <span className={'lf-line lf-line--' + info.tone}>
      <span className={'badge lf-line-badge lf-line-badge--' + info.tone} title={info.why}>{info.label}</span>
      <button type="button" className="lf-why">Why?</button>
      <span className="lf-why-line">{info.why}</span>
    </span>
  );
}

export function LeadFinderView({ api, toast, onOpenLeads }) {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [limit, setLimit] = useState(20);
  const [job, setJob] = useState(null);
  const [jobIds, setJobIds] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importNotice, setImportNotice] = useState(null);
  const [lineTypes, setLineTypes] = useState({});
  const pollRef = useRef(null);
  const linePollRef = useRef(null);
  const listRef = useRef(null);
  const jobIdRef = useRef(null);
  const phonesRef = useRef([]);
  const mountedRef = useRef(true);

  function stopLineTypePoll() {
    if (linePollRef.current) {
      clearTimeout(linePollRef.current);
      linePollRef.current = null;
    }
  }

  function loadStatus() {
    return api('GET', '/api/lead-finder/status')
      .then((s) => { setStatus(s); setStatusError(null); })
      .catch((e) => setStatusError(errorMessage(e, 'Could not load Lead Finder.')));
  }

  useEffect(() => {
    mountedRef.current = true;
    loadStatus();
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
      stopLineTypePoll();
    };
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  function fetchLineTypes(jobId) {
    if (!jobId) return Promise.resolve(null);
    return api('GET', '/api/lead-finder/jobs/' + jobId + '/line-types')
      .then((r) => {
        if (!mountedRef.current || jobIdRef.current !== jobId) return null;
        const map = r && r.line_types && typeof r.line_types === 'object' ? r.line_types : {};
        setLineTypes(map);
        return map;
      })
      .catch(() => null);
  }

  function lineTypesComplete(map) {
    const phones = phonesRef.current;
    if (!phones.length || !map) return false;
    return phones.every((phone) => map[phone] && map[phone].line_type);
  }

  function startLineTypePoll(jobId) {
    stopLineTypePoll();
    const started = Date.now();
    const tick = () => {
      fetchLineTypes(jobId).then((map) => {
        if (!mountedRef.current || jobIdRef.current !== jobId) return;
        if (lineTypesComplete(map)) return;
        if (Date.now() - started >= LINE_TYPE_POLL_BUDGET_MS) return;
        linePollRef.current = setTimeout(tick, LINE_TYPE_POLL_MS);
      });
    };
    tick();
  }

  function applyJob(j) {
    const nextId = j && j.id;
    if (jobIdRef.current !== nextId) {
      stopLineTypePoll();
      setLineTypes({});
      setImportNotice((notice) => (notice && notice.jobId === nextId ? notice : null));
    }
    jobIdRef.current = nextId || null;
    phonesRef.current = ((j && j.leads) || []).map((lead) => lead.phone).filter(Boolean);
    setJob(j);
    const sel = {};
    (j.leads || []).forEach((l, i) => { sel[i] = defaultSelected(l); });
    setSelected(sel);
    if (j && j.status === 'done') fetchLineTypes(j.id);
  }

  function poll(id) {
    api('GET', '/api/lead-finder/jobs/' + id)
      .then((r) => {
        const j = r.job;
        if (j.status === 'running') {
          pollRef.current = setTimeout(() => poll(id), POLL_MS);
          return;
        }
        setBusy(false);
        if (j.status === 'done') {
          applyJob(j);
          setJobIds((ids) => ids.concat([j.id]));
          setMessages((m) => m.concat([{ role: 'assistant', content: j.reply || ('Found ' + j.leads.length + ' businesses.') }]));
        } else {
          setMessages((m) => m.concat([{ role: 'assistant', content: j.error || 'The search failed. Please try again.', error: true }]));
        }
        loadStatus();
      })
      .catch((e) => {
        setBusy(false);
        setMessages((m) => m.concat([{ role: 'assistant', content: errorMessage(e, 'Lost track of the search. Please try again.'), error: true }]));
      });
  }

  function send(text) {
    const content = String(text != null ? text : input).trim();
    if (!content || busy) return;
    const next = messages.filter((m) => !m.error).concat([{ role: 'user', content }]);
    setMessages(messages.concat([{ role: 'user', content }]));
    setInput('');
    setBusy(true);
    api('POST', '/api/lead-finder/search', {
      messages: next.map((m) => ({ role: m.role, content: m.content })),
      limit,
      previous_job_ids: jobIds
    })
      .then((r) => { pollRef.current = setTimeout(() => poll(r.job_id), POLL_MS); })
      .catch((e) => {
        setBusy(false);
        setMessages((m) => m.concat([{ role: 'assistant', content: errorMessage(e, 'Could not start the search.'), error: true }]));
      });
  }

  function newConversation() {
    if (busy) return;
    stopLineTypePoll();
    jobIdRef.current = null;
    phonesRef.current = [];
    setMessages([]);
    setJob(null);
    setJobIds([]);
    setSelected({});
    setImportNotice(null);
    setLineTypes({});
  }

  function importSelected() {
    if (!job) return;
    const indexes = Object.keys(selected).filter((k) => selected[k]).map(Number);
    if (!indexes.length) return;
    const jobId = job.id;
    setImporting(true);
    let importedCount = 0;
    api('POST', '/api/lead-finder/jobs/' + jobId + '/import', { indexes })
      .then((r) => {
        importedCount = Number(r.imported) || 0;
        const parts = [importedCount ? leadsAddedText(importedCount) : 'No new leads added'];
        if (r.skipped_existing) parts.push(r.skipped_existing + (r.skipped_existing === 1 ? ' was already in Leads' : ' were already in Leads'));
        if (r.skipped_dnc) parts.push(r.skipped_dnc + (r.skipped_dnc === 1 ? ' is on your DNC list' : ' are on your DNC list'));
        if (importedCount > 0) {
          setImportNotice({
            jobId,
            count: importedCount,
            skippedExisting: Number(r.skipped_existing) || 0,
            skippedDnc: Number(r.skipped_dnc) || 0
          });
        }
        toast(parts.join('. ') + '.', importedCount ? 'success' : 'info');
        return api('GET', '/api/lead-finder/jobs/' + jobId);
      })
      .then((x) => {
        if (x && x.job) applyJob(x.job);
        if (importedCount > 0) startLineTypePoll(jobId);
      })
      .catch((e) => toast(errorMessage(e, 'Import failed.'), 'error'))
      .then(() => setImporting(false));
  }

  if (statusError) {
    return (
      <section className="card"><div className="card-body">
        <div className="card-title">Lead Finder</div>
        <div className="card-subtitle" style={{ marginTop: 8 }}>{statusError}</div>
        <button type="button" className="button outline small" style={{ marginTop: 12 }} onClick={loadStatus}>Try again</button>
      </div></section>
    );
  }
  if (!status) {
    return <section className="card"><div className="card-body"><div className="muted">Loading Lead Finder…</div></div></section>;
  }
  if (!status.enabled) {
    return (
      <section className="card"><div className="card-body" style={{ padding: '2rem' }}>
        <div className="card-title">Lead Finder <span className="badge new" style={{ marginLeft: 8 }}>Premium</span></div>
        <p className="card-subtitle" style={{ marginTop: 10, maxWidth: 640 }}>
          Describe the businesses you want to call, like “plumbers in San Diego”, and Lead Finder searches the web for real
          listings with phone numbers you can import as leads in one click.
        </p>
        <p className="muted" style={{ marginTop: 10 }}>Lead Finder is included on the Pro and Enterprise plans. Your plan: {status.plan || 'not set'}.</p>
      </div></section>
    );
  }

  const leads = (job && job.leads) || [];
  const selectedCount = leads.filter((_, i) => selected[i]).length;
  const quota = status.quota || { used: 0, limit: 0, remaining: 0 };
  const notice = importNotice && job && importNotice.jobId === job.id && importNotice.count > 0 ? importNotice : null;
  const skipBits = [];
  if (notice && notice.skippedExisting) skipBits.push(notice.skippedExisting + (notice.skippedExisting === 1 ? ' was already in Leads' : ' were already in Leads'));
  if (notice && notice.skippedDnc) skipBits.push(notice.skippedDnc + (notice.skippedDnc === 1 ? ' is on your DNC list' : ' are on your DNC list'));

  return (
    <div className="stack gap-md">
      <section className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="card-title">Lead Finder</div>
            <div className="card-subtitle">Ask for businesses to call. Results come from public web listings with the page each phone number was found on.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="badge new" title="Searches used this month">{quota.used} / {quota.limit} searches this month</span>
            <button type="button" className="button ghost small" onClick={newConversation} disabled={busy || !messages.length}>New search</button>
          </div>
        </div>
        <div className="card-body">
          <div ref={listRef} role="log" aria-live="polite" style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {!messages.length ? (
              <div>
                <div className="muted" style={{ marginBottom: 8 }}>Try one of these:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {EXAMPLES.map((ex) => (
                    <button key={ex} type="button" className="button outline small" onClick={() => send(ex)}>{ex}</button>
                  ))}
                </div>
              </div>
            ) : messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                padding: '8px 12px',
                borderRadius: 10,
                background: m.role === 'user' ? 'rgba(59, 130, 246, 0.18)' : (m.error ? 'rgba(248, 113, 113, 0.14)' : 'rgba(148, 163, 184, 0.12)'),
                whiteSpace: 'pre-wrap'
              }}>{m.content}</div>
            ))}
            {busy ? <div className="muted" style={{ alignSelf: 'flex-start' }}>Searching the web… this usually takes 10–60 seconds.</div> : null}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label className="field" style={{ flex: '1 1 320px', margin: 0 }}>
              <span className="field-label">What kind of businesses, and where?</span>
              <input
                className="input"
                value={input}
                maxLength={1000}
                placeholder={jobIds.length ? 'Refine, e.g. "20 more in Oceanside"' : 'e.g. Roofing contractors in Phoenix, AZ'}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Results</span>
              <select className="select" value={limit} onChange={(e) => setLimit(Number(e.target.value))} disabled={busy}>
                {[10, 20, 30, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button type="submit" className="button primary" disabled={busy || !input.trim() || quota.remaining <= 0}>
              {busy ? 'Searching…' : 'Search'}
            </button>
          </form>
          {quota.remaining <= 0 ? <div className="field-help" style={{ marginTop: 6 }}>You have used all searches for this month.</div> : null}
        </div>
      </section>

      {job && job.status === 'done' ? (
        <section className="card">
          {notice ? (
            <div className="lf-import-banner" role="status" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '4px 4px 12px', padding: '14px 16px', borderRadius: 10, border: '1px solid var(--success, #15803d)', background: 'var(--success-soft, #dcfce7)' }}>
              <div>
                <p className="lf-import-title">{leadsAddedText(notice.count)}</p>
                {skipBits.length ? <p className="lf-import-note">{skipBits.join('. ')}.</p> : null}
              </div>
              <button type="button" className="button primary" onClick={() => { if (typeof onOpenLeads === 'function') onOpenLeads(); }}>Open Leads</button>
            </div>
          ) : null}
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="card-title">{leads.length ? leads.length + ' businesses found' : 'No businesses found'}</div>
              <div className="card-subtitle">Numbers already in your leads, on your DNC list, toll-free, or without a confirmed source are unchecked.</div>
            </div>
            {leads.length ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="button ghost small" onClick={() => { const s = {}; leads.forEach((l, i) => { s[i] = !l.existing_lead_id && !l.dnc; }); setSelected(s); }}>Select all new</button>
                <button type="button" className="button ghost small" onClick={() => setSelected({})}>Clear</button>
                <button type="button" className="button primary small" disabled={!selectedCount || importing} onClick={importSelected}>
                  {importing ? 'Importing…' : 'Import ' + selectedCount + (selectedCount === 1 ? ' lead' : ' leads')}
                </button>
              </div>
            ) : null}
          </div>
          {leads.length ? (
            <>
              <p className="lf-explainer">
                Numbers found are public business listings. Many are cell phones. The AI may only call a number if you have the person's consent on file, or if it's a verified business landline. You or your team can always call by hand.
              </p>
              <div className="table-wrap">
                <table className="table table-compact">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}><span className="sr-only">Select</span></th>
                      <th>Business</th>
                      <th>Phone</th>
                      <th>Phone type</th>
                      <th>Location</th>
                      <th>Category</th>
                      <th>Source</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l, i) => {
                      const st = leadStatus(l);
                      const locked = !!(l.existing_lead_id || l.dnc);
                      return (
                        <tr key={l.phone}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={'Select ' + l.business_name}
                              checked={!!selected[i]}
                              disabled={locked}
                              onChange={(e) => { const v = e.target.checked; setSelected((s) => ({ ...s, [i]: v })); }}
                            />
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{l.business_name}</div>
                            {l.website ? <a href={l.website} target="_blank" rel="noopener noreferrer" className="muted" style={{ fontSize: '0.75rem' }}>{hostLabel(l.website)}</a> : null}
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatPhone(l.phone)}</td>
                          <td><LineTypeBadge phone={l.phone} lineTypes={lineTypes} /></td>
                          <td>{[l.address, l.city, l.state].filter(Boolean).join(', ') || '—'}</td>
                          <td>{l.category || '—'}</td>
                          <td>{l.source_url ? <a href={l.source_url} target="_blank" rel="noopener noreferrer">{hostLabel(l.source_url)}</a> : '—'}</td>
                          <td><span className={'badge ' + st.cls}>{st.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="card-body muted">Try a broader area or a different business type.</div>
          )}
          <div className="card-body" style={{ paddingTop: 8 }}>
            <div className="field-help">
              Importing does not place calls. You are responsible for calling rules: check the National Do Not Call Registry,
              get consent before AI calls to cell phones, and call only 8am–9pm in the business's local time.
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
